// One meeting (brief update 2, section 18; docs/PLAN.md step 5): its agenda (both founders add
// topics; related ones are merged with their authors kept), its notes, and what the notes say as items
// a founder accepts or rejects one by one. Nothing becomes a record, and nothing is sent, by itself.
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useContextWorkspace } from '../components/Layout';
import { Audience, Chip, Demo, Empty, ErrorBox, Field, Loading, PageHead } from '../components/ui';
import { api, errorText } from '../lib/api';
import { day } from '../lib/format';
import { useSession } from '../lib/session';
import type { AgendaTopic, MeetingDetail, MeetingItemData } from '../lib/types';
import { useApi } from '../lib/useApi';

const KINDS: { kind: MeetingItemData['kind']; title: string; accept: string }[] = [
  { kind: 'decision', title: 'Decisions', accept: 'Record the decision' },
  { kind: 'action', title: 'Actions', accept: 'Accept as a task' },
  { kind: 'idea', title: 'Ideas (never tasks by themselves)', accept: 'Keep as an idea' },
  { kind: 'question', title: 'Open questions', accept: 'Put on the next agenda' },
  { kind: 'disagreement', title: 'Disagreements', accept: 'Put on the next agenda' },
  { kind: 'constraint', title: 'Constraints', accept: 'Keep as knowledge' },
];

export function MeetingRoom() {
  const ws = useContextWorkspace()!;
  const { mid } = useParams();
  const path = `/api/w/${ws.slug}/meetings/${mid}`;
  const meeting = useApi<MeetingDetail>(path);
  const m = meeting.data;
  const [error, setError] = useState('');
  const reading = Boolean(m?.extraction && ['queued', 'running'].includes(m.extraction.status));
  const reload = meeting.reload; // stable for a given path (useApi)

  useEffect(() => {
    if (!reading) return;
    const t = window.setInterval(() => void reload(), 2000);
    return () => window.clearInterval(t);
  }, [reading, reload]);

  async function call(url: string, body: Record<string, unknown>, method: 'POST' | 'PATCH' = 'POST') {
    setError('');
    try {
      const out = await api<MeetingDetail>(url, { method, body });
      if (out && 'agenda' in out) meeting.setData(out);
      else void meeting.reload();
    } catch (err) {
      setError(errorText(err));
    }
  }

  if (meeting.error) return <ErrorBox error={meeting.error} retry={meeting.reload} />;
  if (!m) return <Loading what="Loading the meeting" />;
  const planned = m.status === 'planned';

  return (
    <>
      <PageHead
        title={m.title}
        sub={
          <span className="row gap wrap">
            <Chip value={m.status} />
            <span>{m.held_on ? day(m.held_on) : 'no date yet'}</span>
            <Audience item={m} />
            <Demo show={m.demo} />
          </span>
        }
        actions={
          <>
            <Link className="btn" to={`/w/${ws.slug}/meetings`}>
              All meetings
            </Link>
            {planned && (
              <button type="button" className="btn" onClick={() => void call(path, { held: true }, 'PATCH')}>
                Mark as held
              </button>
            )}
          </>
        }
      />
      {!m.delivery.configured && <p className="msg">{m.delivery.reason}</p>}
      {error && <p className="msg err">{error}</p>}

      <section className="panel" aria-label="Agenda">
        <h2>Agenda</h2>
        <p className={`small ${m.agenda_minutes > 60 ? 'msg warn' : 'muted'}`}>
          {m.agenda_minutes} minutes planned for the open topics{m.agenda_minutes > 60 ? ': usually one hour or less' : ''}.
        </p>
        {m.agenda.length ? (
          <ul className="recs">
            {m.agenda.map((t) => (
              <Topic key={t.id} topic={t} others={m.agenda.filter((o) => o.id !== t.id && o.status !== 'merged')} planned={planned} onCall={call} />
            ))}
          </ul>
        ) : (
          <Empty>No topic yet.</Empty>
        )}
        {planned && <AddTopic path={`${path}/agenda`} onCall={call} />}
      </section>

      <section className="panel" aria-label="Notes">
        <h2>Notes</h2>
        {m.notes_text ? (
          <>
            <p className="muted small">
              {m.notes_label} · imported by {m.notes_imported_by ?? 'someone'}
              {m.notes_imported_at ? `, ${day(m.notes_imported_at)}` : ''} · {m.notes_text.length.toLocaleString('en')} characters
            </p>
            <details className="fold">
              <summary>Show the notes</summary>
              <p className="small pre quote">{m.notes_text}</p>
            </details>
            <ReadNotes meeting={m} path={path} onCall={call} />
          </>
        ) : (
          <ImportNotes path={`${path}/notes`} onCall={call} />
        )}
      </section>

      {m.items.length > 0 && (
        <section className="panel" aria-label="What the notes say">
          <h2>What the notes say</h2>
          <p className="muted small">Proposed by the assistant from the notes. Nothing is recorded until you accept it.</p>
          {KINDS.map(({ kind, title, accept }) => {
            const rows = m.items.filter((i) => i.kind === kind);
            if (!rows.length) return null;
            return (
              <div key={kind} className="item-group">
                <h3>{title}</h3>
                <ul className="recs">
                  {rows.map((i) => (
                    <Item key={i.id} item={i} accept={accept} members={m.members} base={`/api/w/${ws.slug}/meeting-items/${i.id}`} onDone={() => void meeting.reload()} />
                  ))}
                </ul>
              </div>
            );
          })}
        </section>
      )}
    </>
  );
}

type Call = (url: string, body: Record<string, unknown>, method?: 'POST' | 'PATCH') => Promise<void>;

function Topic({ topic, others, planned, onCall }: { topic: AgendaTopic; others: AgendaTopic[]; planned: boolean; onCall: Call }) {
  const ws = useContextWorkspace()!;
  const [into, setInto] = useState('');
  const url = `/api/w/${ws.slug}/agenda/${topic.id}`;
  return (
    <li className={`rec topic${topic.status === 'discussed' ? ' done' : ''}`}>
      <div className="rec-main">
        <span className="rec-title">{topic.subject}</span>
        <div className="rec-meta">
          <Chip value={topic.goal} />
          {topic.minutes && <span className="muted small">{topic.minutes} min</span>}
          {topic.raised_by && <span className="muted small">raised by {topic.raised_by}</span>}
          {topic.carried && <span className="badge">carried</span>}
          {topic.status === 'discussed' && <Chip value="discussed" />}
          <Demo show={topic.demo} />
        </div>
        {topic.why && <p className="small">Why: {topic.why}</p>}
        {topic.expected_outcome && <p className="small">Outcome wanted: {topic.expected_outcome}</p>}
        {topic.material && <p className="small muted">Material: {topic.material}</p>}
        {topic.merged.length > 0 && (
          <ul className="merged small">
            {topic.merged.map((c) => (
              <li key={c.id}>
                Also raised: <strong>{c.subject}</strong> by {c.raised_by ?? 'someone'}
              </li>
            ))}
          </ul>
        )}
        {planned && (
          <div className="row gap wrap">
            <button type="button" className="btn small" onClick={() => void onCall(url, { status: topic.status === 'discussed' ? 'open' : 'discussed' }, 'PATCH')}>
              {topic.status === 'discussed' ? 'Not discussed yet' : 'Discussed'}
            </button>
            {others.length > 0 && (
              <>
                <select aria-label={`Merge “${topic.subject}” into`} value={into} onChange={(e) => setInto(e.target.value)}>
                  <option value="">Merge into…</option>
                  {others.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.subject}
                    </option>
                  ))}
                </select>
                <button type="button" className="btn small" disabled={!into} onClick={() => void onCall(url, { merge_into: into }, 'PATCH')}>
                  Merge
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

function AddTopic({ path, onCall }: { path: string; onCall: Call }) {
  const empty = { subject: '', why: '', goal: 'explore', expected_outcome: '', minutes: '', material: '' };
  const [f, setF] = useState(empty);
  return (
    <form
      aria-label="Add a topic"
      onSubmit={(e) => {
        e.preventDefault();
        void onCall(path, { ...f, minutes: f.minutes ? Number(f.minutes) : undefined }).then(() => setF(empty));
      }}
    >
      <h3>Add a topic</h3>
      <Field label="Subject or question">
        <input required value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} maxLength={300} />
      </Field>
      <div className="row gap wrap">
        <Field label="Goal">
          <select value={f.goal} onChange={(e) => setF({ ...f, goal: e.target.value })}>
            <option value="explore">Explore</option>
            <option value="decide">Decide</option>
            <option value="review">Review</option>
          </select>
        </Field>
        <Field label="Minutes">
          <input type="number" min={1} max={240} value={f.minutes} onChange={(e) => setF({ ...f, minutes: e.target.value })} />
        </Field>
      </div>
      <Field label="Why it matters">
        <textarea rows={2} value={f.why} onChange={(e) => setF({ ...f, why: e.target.value })} />
      </Field>
      <Field label="Outcome wanted">
        <input value={f.expected_outcome} onChange={(e) => setF({ ...f, expected_outcome: e.target.value })} />
      </Field>
      <Field label="Supporting material (links or notes)">
        <input value={f.material} onChange={(e) => setF({ ...f, material: e.target.value })} />
      </Field>
      <button type="submit" className="btn primary" disabled={!f.subject.trim()}>
        Add the topic
      </button>
    </form>
  );
}

function ImportNotes({ path, onCall }: { path: string; onCall: Call }) {
  const [text, setText] = useState('');
  const [label, setLabel] = useState('');
  return (
    <form
      aria-label="Import the notes"
      onSubmit={(e) => {
        e.preventDefault();
        void onCall(path, { text, label: label || undefined });
      }}
    >
      <p className="muted small">Paste the notes or transcript, or open a text file (for example a Granola export saved as text).</p>
      <Field label="Notes or transcript">
        <textarea rows={8} value={text} onChange={(e) => setText(e.target.value)} name="notes" />
      </Field>
      <div className="row gap wrap">
        <Field label="Or open a text file">
          <input
            type="file"
            accept=".txt,.md,text/plain,text/markdown"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              void file.text().then((t) => {
                setText(t);
                setLabel(file.name);
              });
            }}
          />
        </Field>
        <Field label="Where they come from (optional)">
          <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={200} placeholder="Granola export" />
        </Field>
      </div>
      <button type="submit" className="btn primary" disabled={!text.trim()}>
        Import the notes
      </button>
    </form>
  );
}

function ReadNotes({ meeting, path, onCall }: { meeting: MeetingDetail; path: string; onCall: Call }) {
  const { me } = useSession();
  const [model, setModel] = useState(me.models.find((x) => x.default)?.id ?? me.models[0]?.id ?? '');
  const ex = meeting.extraction;
  if (ex && ['queued', 'running'].includes(ex.status)) {
    return (
      <p className="msg" role="status">
        <span className="spinner" aria-hidden="true" /> The assistant is reading the notes ({ex.model})…
      </p>
    );
  }
  if (ex && ex.status === 'succeeded' && (ex.item_count ?? 0) > 0) {
    return (
      <p className="muted small" role="status">
        Read by {ex.model}: {ex.item_count} item(s) to review below{ex.cost_usd != null ? `, cost $${ex.cost_usd.toFixed(4)}` : ''}.
      </p>
    );
  }
  return (
    <div className="row gap wrap">
      {ex && ex.status !== 'succeeded' && <p className="msg err">The last reading {ex.status}: {ex.error}</p>}
      {ex && ex.status === 'succeeded' && <p className="msg">The last reading found nothing to review.</p>}
      <Field label="Model">
        <select value={model} onChange={(e) => setModel(e.target.value)} name="model">
          {me.models.map((x) => (
            <option key={x.id} value={x.id}>
              {x.label}
            </option>
          ))}
        </select>
      </Field>
      <button type="button" className="btn primary" onClick={() => void onCall(`${path}/extract`, { model })}>
        Read the notes
      </button>
      <p className="muted small">One Claude Code session reads only these notes (no other records, no tools) and lists what they say for you to review.</p>
    </div>
  );
}

function Item({
  item,
  accept,
  members,
  base,
  onDone,
}: {
  item: MeetingItemData;
  accept: string;
  members: { id: string; name: string }[];
  base: string;
  onDone: () => void;
}) {
  const ws = useContextWorkspace()!;
  const [error, setError] = useState('');
  const proposed = item.review_status === 'proposed';

  async function send(url: string, body?: Record<string, unknown>, method: 'POST' | 'PATCH' = 'POST') {
    setError('');
    try {
      await api(url, { method, body: body ?? {} });
      onDone();
    } catch (err) {
      setError(errorText(err));
    }
  }

  const [kind, id] = (item.result_ref ?? ':').split(':');
  const href =
    kind === 'task'
      ? `/w/${ws.slug}/projects?tab=tasks&open=${id}`
      : kind === 'decision'
        ? `/w/${ws.slug}/decisions?open=${id}`
        : kind === 'idea'
          ? `/w/${ws.slug}/ideas?open=${id}`
          : kind === 'knowledge'
            ? `/w/${ws.slug}/projects?tab=knowledge&open=${id}`
            : `/w/${ws.slug}/meetings`;

  return (
    <li className={`rec item ${item.review_status}`}>
      <div className="rec-main">
        <span className="rec-title">{item.text}</span>
        {item.source_quote && <blockquote className="small quote">“{item.source_quote}”</blockquote>}
        {item.uncertain && item.uncertainty && <p className="small msg warn">To check: {item.uncertainty}</p>}
        {item.kind === 'action' && proposed && (
          <div className="row gap wrap">
            <Field label="Owner" hint={item.owner_label ? `The notes say: ${item.owner_label}` : undefined}>
              <select
                value={item.assignee?.id ?? ''}
                onChange={(e) => void send(base, e.target.value ? { assignee_user_id: e.target.value } : { clear_assignee: true }, 'PATCH')}
              >
                <option value="">Nobody yet</option>
                {members.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Due" hint={item.due_label ? `The notes say: ${item.due_label}` : undefined}>
              <input
                type="date"
                value={item.due_date ?? ''}
                onChange={(e) => void send(base, e.target.value ? { due_date: e.target.value } : { clear_due: true }, 'PATCH')}
              />
            </Field>
          </div>
        )}
        {proposed ? (
          <div className="row gap wrap">
            <button type="button" className="btn small primary" onClick={() => void send(`${base}/accept`)}>
              {accept}
            </button>
            <button type="button" className="btn small ghost" onClick={() => void send(`${base}/reject`)}>
              Reject
            </button>
          </div>
        ) : item.review_status === 'accepted' ? (
          <p className="small msg ok">
            Accepted by {item.reviewed_by ?? 'someone'}: <Link to={href}>{kind === 'agenda' ? 'waiting for the next meeting' : `open the ${kind}`}</Link>
          </p>
        ) : (
          <p className="small muted">Rejected by {item.reviewed_by ?? 'someone'}.</p>
        )}
        {error && <p className="msg err">{error}</p>}
      </div>
    </li>
  );
}
